import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Card,
  CardBody,
  Form,
  Label,
  Input,
  FormFeedback,
  Button,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useTranslation } from "react-i18next";
import AuthPageLayout from "../../layouts/VerticalLayouts/AuthPageLayout";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { translateApiError } from "../../helpers/translateApiError";

// Local fallback messages kept in English so the "expired" check below still
// works; they are translated when rendered.
const INVALID_LINK_MESSAGE = "Invalid or expired invitation link";
const VALIDATE_FAILED_MESSAGE =
  "Failed to validate invitation link. Please try again or contact support.";

const ActivateAccount = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    document.title = t("common.pageTitle", {
      page: t("auth.activate.pageTitle"),
    });
  }, [t]);

  useEffect(() => {
    const validateToken = async () => {
      try {
        setValidating(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/activate/${token}/validate`
        );

        const data = response.data || response;

        if (data?.success && data?.valid) {
          // Token is valid, allow user to proceed
          setIsValid(true);
        } else {
          // Token is expired or invalid
          setIsValid(false);
          setValidationMessage(data?.message || INVALID_LINK_MESSAGE);
        }
      } catch (err) {
        console.error("Token validation error:", err);
        setIsValid(false);
        // api_helper rejects with the translated text in err.message and the
        // raw backend response in err.data; keep the raw English for the check below.
        setValidationMessage(
          err?.data?.message ||
            err?.response?.data?.message ||
            err?.message ||
            VALIDATE_FAILED_MESSAGE
        );
      } finally {
        setValidating(false);
      }
    };

    if (token) {
      validateToken();
    }
  }, [token]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, t("auth.activate.validation.minLength"))
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          t("auth.activate.validation.complexity")
        )
        .required(t("auth.activate.validation.passwordRequired")),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], t("auth.activate.validation.mustMatch"))
        .required(t("auth.activate.validation.confirmRequired")),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError("");

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/activate/${token}`,
          {
            password: values.password,
          }
        );


        // The api_helper might unwrap the response
        const data = response.data || response;

        // Check if activation was successful
        if (data?.success || response.status === 200) {
          setSuccess(true);
          toast.success(t("auth.activate.success"), {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          setTimeout(() => {
            window.location.href = "/login";
          }, 3000);
        } else {
          throw new Error(t("auth.activate.failed"));
        }
      } catch (err) {
        console.error("Activation error:", err);
        const rawMessage =
          err?.response?.data?.body?.message ||
          err?.response?.data?.message ||
          err?.body?.message ||
          err?.message;
        const errorMessage = rawMessage
          ? translateApiError(rawMessage)
          : t("auth.activate.error");
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <React.Fragment>
      <AuthPageLayout>
        <ToastContainer />
        <Card className="mt-4">
          <CardBody className="p-4">
            {validating ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <span
                    className="spinner-border text-primary"
                    style={{ width: "3rem", height: "3rem" }}
                  />
                </div>
                <h5 className="text-muted">{t("auth.activate.validating")}</h5>
              </div>
            ) : !isValid ? (
              <div className="text-center">
                <div className="mb-4">
                  <i
                    className="ri-error-warning-line"
                    style={{ fontSize: "72px", color: "#dc3545" }}
                  />
                </div>
                <h4 className="mb-3">
                  {validationMessage.includes("expired")
                    ? t("auth.activate.linkExpired")
                    : t("auth.activate.linkInvalid")}
                </h4>
                <p className="text-muted mb-4">
                  {validationMessage === INVALID_LINK_MESSAGE
                    ? t("auth.activate.invalidOrExpired")
                    : validationMessage === VALIDATE_FAILED_MESSAGE
                    ? t("auth.activate.validateFailed")
                    : translateApiError(validationMessage)}
                </p>
                <div className="d-flex gap-2 justify-content-center">
                  <Link to="/login" className="btn btn-primary">
                    {t("auth.actions.goToLogin")}
                  </Link>
                </div>
              </div>
            ) : success ? (
              <div className="text-center">
                <div className="mb-4">
                  <i
                    className="ri-checkbox-circle-line"
                    style={{ fontSize: "72px", color: "#28a745" }}
                  />
                </div>
                <h4 className="mb-3">{t("auth.activate.activatedTitle")}</h4>
                <p className="text-muted mb-4">
                  {t("auth.activate.activatedText")}
                </p>
                <Link to="/login" className="btn btn-success">
                  {t("auth.actions.goToLogin")}
                </Link>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <h3 className="text-primary fw-semibold mb-2 auth-form-heading">
                    {t("auth.activate.heading")}
                  </h3>
                  <p className="text-muted mb-0 auth-form-subheading">
                    {t("auth.activate.subheading")}
                  </p>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                <Form onSubmit={validation.handleSubmit}>
                  <div className="mb-3">
                    <Label htmlFor="password" className="form-label">
                      {t("auth.fields.password")}{" "}
                      <span className="text-danger">*</span>
                    </Label>
                    <div className="position-relative auth-pass-inputgroup">
                      <Input
                        type={showPassword ? "text" : "password"}
                        className="form-control pe-5 password-input"
                        placeholder={t("auth.fields.passwordPlaceholder")}
                        id="password"
                        name="password"
                        value={validation.values.password}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          validation.touched.password &&
                          validation.errors.password
                            ? true
                            : false
                        }
                      />
                      <button
                        className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted password-addon"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <i
                          className={
                            showPassword ? "ri-eye-off-fill" : "ri-eye-fill"
                          }
                        />
                      </button>
                      {validation.touched.password &&
                        validation.errors.password && (
                          <FormFeedback type="invalid">
                            {validation.errors.password}
                          </FormFeedback>
                        )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="confirmPassword" className="form-label">
                      {t("auth.activate.confirmPassword")}{" "}
                      <span className="text-danger">*</span>
                    </Label>
                    <div className="position-relative auth-pass-inputgroup">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        className="form-control pe-5 password-input"
                        placeholder={t(
                          "auth.activate.confirmPasswordPlaceholder"
                        )}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={validation.values.confirmPassword}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          validation.touched.confirmPassword &&
                          validation.errors.confirmPassword
                            ? true
                            : false
                        }
                      />
                      <button
                        className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted password-addon"
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        <i
                          className={
                            showConfirmPassword
                              ? "ri-eye-off-fill"
                              : "ri-eye-fill"
                          }
                        />
                      </button>
                      {validation.touched.confirmPassword &&
                        validation.errors.confirmPassword && (
                          <FormFeedback type="invalid">
                            {validation.errors.confirmPassword}
                          </FormFeedback>
                        )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                      <strong>{t("auth.activate.requirementsTitle")}</strong>
                      <ul className="mb-0 mt-1">
                        <li>{t("auth.activate.requirements.length")}</li>
                        <li>{t("auth.activate.requirements.case")}</li>
                        <li>{t("auth.activate.requirements.number")}</li>
                      </ul>
                    </p>
                  </div>

                  <div className="mt-4">
                    <Button
                      color="success"
                      className="w-100"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          {t("auth.activate.activating")}
                        </>
                      ) : (
                        t("auth.activate.submit")
                      )}
                    </Button>
                  </div>
                </Form>

                <div className="mt-4 text-center">
                  <p className="mb-0">
                    {t("auth.links.alreadyHaveAccount")}
                    <Link
                      to="/login"
                      className="fw-semibold text-primary text-decoration-underline"
                    >
                      {t("auth.actions.signInTitleCase")}
                    </Link>
                  </p>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </AuthPageLayout>
    </React.Fragment>
  );
};

export default ActivateAccount;
